import { H6, H5 } from "@sellar-io/components";
import {
  OrderFulfillmentType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import React from "react";
import { getTextColorFromOrderStage, OrderStage } from "./helpers";
import { getNextDirectDeliveryDate } from "../../pages/order/helpers/DeliveryDateHelpers/getNextDeliveryDate";
import { formatDate } from "../../utils/formatDate";
import { useDeliveryOptions } from "../../hooks/useDeliveryOptions";
import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";
import { SupplierOnly } from "../authFilter/SupplierOnly";

export type OrderDeliveryDateProps = {
  deliveryDate?: Date;
  dispatchDate?: Date;
  distributionTimePreference?: "am" | "pm" | "allDay" | null;
  stage: OrderStage;
  fulfillmentType: OrderFulfillmentType;
  supplier?: string;
  buyerPostcode: string;
  supplierId: number;
  deliveryArea?: SupplierStorefrontV2GetResponseType["DeliveryArea"];
  isSellarImportOrder: boolean;
  supplierCutOffDate?: Date | null;
};

const renderDeliveryDate = (
  props: OrderDeliveryDateProps,
  dispatchDate?: boolean,
): React.ReactElement => {
  const stageTextColourClass = getTextColorFromOrderStage(props.stage);
  const scheduledDate = dispatchDate ? props.dispatchDate : props.deliveryDate;
  const scheduledDateIsPast = scheduledDate
    ? dayjs(scheduledDate).isBefore(dayjs(), "day")
    : false;
  const scheduledDateClassName = twMerge(
    stageTextColourClass,
    (scheduledDateIsPast || props.stage === "Voided") &&
      "text-zinc-500 line-through",
  );

  const deliveryPreference = () => {
    if (!props.distributionTimePreference) return "";
    if (props.distributionTimePreference === "am") return "- AM";
    if (props.distributionTimePreference === "pm") return "- Early PM";
    if (props.distributionTimePreference === "allDay") return "- All day";
  };

  if (props.stage === "Cancelled") {
    return <H6 className={stageTextColourClass}>Order cancelled</H6>;
  }

  if (props.deliveryDate && !dispatchDate) {
    return (
      <H6 className={scheduledDateClassName}>
        {formatDate(props.deliveryDate, { format: "DD/MM/YY" })}{" "}
        {props.fulfillmentType === "sellarDistribution" && deliveryPreference()}
      </H6>
    );
  }

  if (props.dispatchDate && dispatchDate) {
    return (
      <H6 className={scheduledDateClassName}>
        {formatDate(props.dispatchDate, { format: "DD/MM/YY" })}
        <SupplierOnly>
          {props.fulfillmentType === "sellarDistribution" &&
            props.supplierCutOffDate &&
            (props.stage === "Requested" || props.stage === "Accepted") &&
            !scheduledDateIsPast &&
            ` - Cut-off ${dayjs(props.supplierCutOffDate).format("ddd hh:mma")}`}
        </SupplierOnly>
      </H6>
    );
  }

  // TODO: for sd orders refactor this to correctly account for ooo, pre-order items etc. see DistributionDeliveryContentCompact for reference
  let nextDeliveryDates = props.deliveryArea
    ? getNextDirectDeliveryDate({
        deliveryArea: props.deliveryArea,
        startDateReference: new Date(),
      })
    : null;

  if (
    !props.isSellarImportOrder &&
    ["Accepted", "Requested"].includes(props.stage) &&
    ((nextDeliveryDates?.deliveryDate && !dispatchDate) ||
      (nextDeliveryDates?.dispatchDate && dispatchDate))
  ) {
    return (
      <H5 className={stageTextColourClass}>
        {formatDate(
          dispatchDate
            ? (nextDeliveryDates.dispatchDate ?? null)
            : nextDeliveryDates.deliveryDate,
          { format: "DD/MM/YY" },
        )}
      </H5>
    );
  }

  return (
    <H5 className={stageTextColourClass}>
      TBC by {props.supplier ?? "supplier"}
    </H5>
  );
};

export const OrderDeliveryDate = (
  props: OrderDeliveryDateProps,
): React.ReactElement => {
  const { deliveryAreas } = useDeliveryOptions({
    postcode: props.buyerPostcode,
  });

  const deliveryArea = deliveryAreas?.find(
    (area) =>
      area.systemDefinedType ===
      (props.fulfillmentType === "sellarDistribution"
        ? "sellarDistribution"
        : null),
  );

  return (
    <div className="flex flex-col gap-2">
      <H5>
        {["collection", "sellarDistribution"].includes(props.fulfillmentType)
          ? "Collection date"
          : "Date"}
      </H5>
      <div>
        {renderDeliveryDate(
          {
            ...props,
            deliveryArea: deliveryArea ?? props.deliveryArea,
          },
          props.fulfillmentType === "sellarDistribution",
        )}
      </div>
      {props.fulfillmentType === "sellarDistribution" && (
        <>
          <H5>Delivery date</H5>
          <div>
            {renderDeliveryDate({
              ...props,
              deliveryArea: deliveryArea ?? props.deliveryArea,
            })}
          </div>
        </>
      )}
    </div>
  );
};
